<template>
  <div>
    <Loading text="Loading page details..." v-if="!loadStatus" />

    <template v-else>
      <div class="holder">
        <h2 class="page-titles">{{ groupOrderDetails.name }}</h2>
      </div>

      <GroupOrderHeaderBanner
        :title="'Review'"
        :headerText="'Products'"
        :currentStep="creatingGroupOrderStep"
        @onPrevStep="$router.go(-1)"
        @onNextStep="updateGroupOrder(true)"
      />

      <div class="header-region">
        <div
          class="hidden flex items-center justify-between gap-2 items-center"
        >
          <div class="flex gap-8">
            <div class="toggle-price">
              <span class="flex items-center text-holder">Show prices </span>

              <label class="toggle-button">
                <input
                  type="checkbox"
                  :checked="groupOrderDetails.showPrices"
                  @click.prevent="
                    toggleGroupOrderShowPrices(
                      !groupOrderDetails.showPrices,
                      false
                    )
                  "
                />
                <span class="toggle-slider round"></span>
              </label>
            </div>
            <div class="toggle-price">
              <span class="flex items-center text-holder">Auto-Finalize </span>
              <label class="toggle-button">
                <input
                  :aria-checked="instantFinalizeState"
                  type="checkbox"
                  class="instant-finalize-input"
                  :checked="instantFinalizeState"
                  @click.prevent="instantFinalize($event)"
                  :class="{
                    'button-gemnote--disabled': !!instantFinalizeState
                  }"
                />
                <span class="toggle-slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Products here -->
      <div class="review-sections">
        <table>
          <thead>
            <tr>
              <th class="product-header">Tier</th>
              <th class="product-title">Product</th>
              <th class="product-header"></th>
              <th class="product-size">Product name</th>
              <th class="product-header"></th>
              <th class="product-header"></th>
              <th class="product-quantity">Price</th>
              <th class="product-header"></th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="product in selectedProducts.filter(
                item => !isCard(item.tag_list)
              )"
              :key="product.id"
            >
              <td>{{ product.tier }}</td>
              <td>
                <div class="img-holder">
                  <img :src="product.image" @error="handleBrokenImage" alt="" />
                </div>
              </td>
              <td class="blank"></td>

              <td>{{ product.name || '' }}</td>
              <td class="blank"></td>
              <td class="blank"></td>

              <td>
                <span v-if="product.price">
                  ${{ product.price || '' | formatCurrency }}</span
                >
                <span v-else></span>
              </td>
              <td class="blank"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Card here -->
      <div v-if="selectedCard.length" class="review-sections card-review">
        <GroupOrderTierHeader
          :title="`Cards`"
          :disabled="true"
          :isGroupOrderTier="false"
        />

        <table style="margin-top: 0.5rem">
          <thead>
            <tr>
              <th class="product-title">Product</th>
              <th class="product-size">Product Name</th>
              <th class="product-header">Message</th>
              <th class="product-quantity">Price</th>
              <th class="product-title"></th>
              <th class="product-title"></th>
              <th class="product-title"></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(card, i) in selectedCard" :key="i">
              <!-- <td>1</td> -->
              <td>
                <div class="img-holder cards">
                  <img
                    :src="card && card.image ? card.image.url : ''"
                    :alt="card && card.image ? card.image.filename : ''"
                    @error="handleBrokenImage($event)"
                  />
                </div>
              </td>
              <td>
                <div class="w-2/3">
                  {{
                    card && card.attributes ? card.attributes.displayName : ''
                  }}
                </div>
              </td>
              <td>
                {{
                  card && groupOrderDetails.cardMessage
                    ? groupOrderDetails.cardMessage
                    : ''
                }}
              </td>
              <td>
                ${{
                  card && card.attributes
                    ? card.attributes.price
                    : '' | formatCurrency
                }}
              </td>
              <td class="blank"></td>
              <td class="blank"></td>
              <td class="blank"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Information Fields here -->
      <div class="review-sections">
        <GroupOrderTierHeader
          :title="`Settings`"
          :disabled="true"
          :isGroupOrderTier="false"
        />

        <div class="info-holder">
          <!-- Required Field here -->
          <div class="info">
            <section class="general-settings">
              <div class="info-holder-header"><strong>General</strong></div>
            </section>
            <div class="required-info margin-bottom-1">
              <div class="title">
                <p><strong>Show prices </strong></p>
              </div>

              <div class="detail">
                <label class="toggle-button">
                  <input
                    type="checkbox"
                    :checked="groupOrderDetails.showPrices"
                    @click.prevent="
                      toggleGroupOrderShowPrices(
                        !groupOrderDetails.showPrices,
                        false
                      )
                    "
                  />
                  <span class="toggle-slider round"></span>
                </label>
              </div>
            </div>
            <div class="required-info">
              <div class="title">
                <p><strong>Auto-finalize </strong></p>
              </div>

              <div class="detail">
                <label class="toggle-button">
                  <input
                    :aria-checked="instantFinalizeState"
                    type="checkbox"
                    class="instant-finalize-input"
                    :checked="instantFinalizeState"
                    @click.prevent="instantFinalize($event)"
                    :class="{
                      'button-gemnote--disabled': !!instantFinalizeState
                    }"
                  />
                  <span class="toggle-slider round"></span>
                </label>
              </div>
            </div>
          </div>

          <!-- Title here -->
          <div class="info info-margin">
            <section class="general-settings">
              <div class="info-holder-header"><strong>Custom</strong></div>
              <div class="info-holder-sub-header">Cover Page</div>
            </section>
            <div class="required-info">
              <div class="title">
                <p><strong>Heading </strong></p>
              </div>

              <div class="detail">
                <p class="display-text">{{ defaultTitle }}</p>
              </div>
            </div>
            <div class="required-info margin-top-1">
              <div class="title">
                <p><strong>Description </strong></p>
              </div>

              <div class="detail">
                <section
                  class="w-6/6"
                  v-if="!groupOrderDetails.enableCustomDescription"
                >
                  <p class="display-text">
                    You've been invited by {{ groupOrderDetails.teamName }} to
                    redeem free merchandise for {{ teamName }} Thank you for
                    being a valued partner.
                  </p>
                </section>

                <section v-else>
                  <p class="display-text">
                    {{ groupOrderDetails.redemptionDescription }}
                  </p>
                </section>
              </div>
            </div>
          </div>
          <div class="info info-margin">
            <section class="general-settings">
              <div class="info-holder-sub-header">Product Selection(s)</div>
            </section>
            <div class="required-info">
              <div class="title">
                <p><strong>Additional Instructions </strong></p>
              </div>

              <div class="detail">
                <p class="display-text">
                  {{ defaultSubTitle }}
                </p>
              </div>
            </div>
          </div>
          <div class="info info-margin">
            <section class="general-settings">
              <div class="info-holder-sub-header">Data Collection</div>
            </section>
            <div class="required-info">
              <div class="title">
                <p><strong>Custom fields </strong></p>
              </div>

              <div class="detail custom-fields">
                <ul
                  class="grid grid-rows-2 grid-cols-3 md:grid-cols-8 grid-flow-col"
                >
                  <li
                    v-for="(field, index) in groupOrderDetails.customFields"
                    :key="index"
                  >
                    {{ field.name }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="info info-margin">
            <section class="general-settings">
              <div class="info-holder-header"><strong>Page Access</strong></div>
            </section>
            <div class="required-info">
              <div class="title">
                <p><strong>Sign-in method </strong></p>
              </div>

              <div class="detail">
                <p class="display-text">Emails from an allow list</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import GroupOrderHeaderBanner from './GroupOrderHeaderBanner'
import GroupOrderTierHeader from './GroupOrderTierHeader'
import { mapState } from 'vuex'
import {
  layouts,
  progressStatus,
  isCard,
  handleBrokenImage
} from '../../helpers'

export default {
  name: 'ReviewGroupOrder',
  props: ['id', 'isEdit'],

  components: { GroupOrderHeaderBanner, GroupOrderTierHeader },

  data() {
    return {
      customFields: [],
      selectedProducts: [],
      handleBrokenImage: handleBrokenImage
    }
  },

  computed: {
    instantFinalizeState() {
      switch (this.groupOrderDetails.state) {
        case 'pending':
          return false
        case 'locked':
          return false
        case 'finalized':
          return false
        case 'auto_finalising':
          return true
        case 'validating_recipients':
          return true
        case 'validating_subscription':
          return true
        case 'lock_finalisation':
          return false
        default:
          return false
      }
    },
    loadStatus() {
      return progressStatus(this.loadGroupOrder)
    },
    variantId() {
      return this.groupOrderDetails &&
        this.groupOrderDetails.cardsBlob &&
        this.groupOrderDetails.cardsBlob[0]
        ? this.groupOrderDetails.cardsBlob[0].productVariantId
        : ''
    },
    selectedCard() {
      return this.cardList.filter(card => card.id === this.variantId)
    },

    defaultTitle() {
      const enableCustomTitle = this.groupOrderDetails.enableCustomTitle

      if (enableCustomTitle) {
        return this.groupOrderDetails.redemptionTitle
      } else {
        return `Welcome to ${this.groupOrderDetails.name} for ${this.groupOrderDetails.teamName}`
      }
    },

    defaultSubTitle() {
      return this.groupOrderDetails.redemptionSubheading
        ? this.groupOrderDetails.redemptionSubheading
        : 'N/A'
    },

    ...mapState({
      loadGroupOrder: state => state.groupOrder.loadGroupOrder,
      groupOrderDetails: state => state.groupOrder.groupOrderDetails,
      groupOrderTiers: state => state.groupOrder.groupOrderTiers,
      cardList: state => state.cardTemplate.cardList,
      cardMessage: state => state.groupOrder.groupOrderDetails.cardMessage,
      creatingGroupOrderStep: state => state.groupOrder.creatingGroupOrderStep,
      teamName: state => state.team.team.name
    })
  },

  created() {
    this.$store.dispatch('groupOrder/getGroupOrder', this.id)
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
    this.$store.dispatch('cardTemplate/getCards')
    this.$store.commit('groupOrder/CREATING_GROUP_ORDER_STEP', 7)
    this.organizeTiers()
  },

  methods: {
    instantFinalize() {
      if (this.instantFinalizeState) {
        this.$store.dispatch(
          'groupOrder/stopInstantFinalize',
          this.groupOrderDetails.id
        )
      } else {
        this.$store.dispatch(
          'groupOrder/startInstantFinalize',
          this.groupOrderDetails.id
        )
      }
    },
    addCustomField() {
      this.customFields.push({ name: '', type: 'text' })
    },
    isCard(tagList) {
      return isCard(tagList)
    },
    updateGroupOrder(redirect) {
      this.$store.dispatch('groupOrder/updateGroupOrder', {
        customFields: [
          ...this.groupOrderDetails.customFields,
          ...this.customFields
        ],
        redirect: redirect,
        showPrices: this.groupOrderDetails.showPrices,
        password: this.groupOrderDetails.password,
        passwordProtected: this.groupOrderDetails['passwordProtected?'],
        redemptionTitle: this.groupOrderDetails.redemptionTitle,
        redemptionDescription: this.groupOrderDetails.redemptionDescription,
        enableCustomTitle: this.groupOrderDetails.enableCustomTitle,
        enableCustomSubTitle: this.groupOrderDetails.enableCustomSubheading,
        redemptionSubTitle: this.groupOrderDetails.redemptionSubheading,
        enableCustomDescription: this.groupOrderDetails.enableCustomDescription
      })
      localStorage.removeItem('selecteCard')
    },
    toggleGroupOrderShowPrices(showPrices, redirect) {
      this.$store.dispatch('groupOrder/updateGroupOrder', {
        customFields: [
          ...this.groupOrderDetails.customFields,
          ...this.customFields
        ],
        redirect: redirect,
        showPrices,
        password: this.groupOrderDetails.password,
        passwordProtected: this.groupOrderDetails['passwordProtected?'],
        redemptionTitle: this.groupOrderDetails.redemptionTitle,
        redemptionDescription: this.groupOrderDetails.redemptionDescription,
        enableCustomTitle: this.groupOrderDetails.enableCustomTitle,
        enableCustomDescription: this.groupOrderDetails.enableCustomDescription
      })
    },
    organizeTiers() {
      // list and organize tiers
      if (!this.loadStatus) {
        for (let i = 0; i <= this.groupOrderTiers.length - 1; i++) {
          const data =
            this.groupOrderTiers &&
            this.groupOrderTiers[i] &&
            this.groupOrderTiers[i]
              ? this.groupOrderTiers[i].tierItems
              : []
          const singleTier = data.map(tier => {
            return {
              tier: i + 1,
              id: tier.itemId || '',
              image:
                tier.itemableAttributes.images &&
                tier.itemableAttributes.images[0]
                  ? tier.itemableAttributes.images[0].url
                  : (tier.itemableAttributes.default_product_variant_images &&
                      tier.itemableAttributes
                        .default_product_variant_images[0] &&
                      tier.itemableAttributes.default_product_variant_images[0]
                        .url) ||
                    '',
              name:
                tier && tier.itemableAttributes
                  ? tier.itemableAttributes.name
                  : '',
              price:
                tier && tier.itemableAttributes
                  ? tier.itemableAttributes.retail_price
                  : tier.itemableAttributes.lowest_price || '',
              tag_list:
                (tier &&
                  tier.itemableAttributes &&
                  tier.itemableAttributes.tag_list) ||
                []
            }
          })

          this.selectedProducts.push(...singleTier)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.margin {
  margin-bottom: 1.5rem;
}

// .header-region {
//   p {
//     text-align: center;
//     padding-bottom: 0.6rem;
//     font-size: 1.3rem;
//     font-weight: 400;
//   }
// }

.toggle-price {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  font-size: 21px;

  span {
    font-weight: 500;
    font-size: 0.875rem;
  }
}

.img-holder {
  width: 98px;
  height: 98px;
  max-width: 100%;
  margin: auto;

  &.cards {
    width: 98px;
    height: 98px;
  }

  img {
    width: inherit;
    height: inherit;
    object-fit: contain;
  }
}

.review-sections {
  margin: 0rem 0rem 0rem 0rem;
  text-align: left;

  .captions {
    font-size: 0.938rem;
    color: #25282a;
    font-weight: bold;
  }

  &.card-review {
    table {
      tbody > tr > td {
        padding: 1rem 0;
      }
      thead > tr > th {
        &:nth-child(2) {
          text-align: left;
          padding-left: 1rem;
          padding-right: 1rem;
        }
        &:nth-child(3) {
          text-align: left;
          // padding-left: 2rem;
        }
      }
      tbody > tr > td {
        &:nth-child(1) {
          width: 8rem;
          max-width: 8rem;
        }
        &:nth-child(2) {
          // width: 13rem;
          // max-width: 13rem;
          text-align: left;
          padding-left: 1rem;
          padding-right: 1rem;
        }
        &:nth-child(3) {
          width: 32rem;
          max-width: 32rem;
          // padding-left: 2rem;
        }
      }
    }
  }
}

.holder {
  text-align: left;

  h3 {
    font-size: 1.063rem;
    font-family: $inter;
    color: $main-6;
    padding-bottom: 2rem;
    padding-top: 0.5rem;

    span {
      font-weight: 400;
    }
  }
}

.info-holder {
  margin: 2rem 0rem;
  padding: 2rem 1.5rem;
  border: 1px solid #232220;
  border-radius: 12px;

  .required-info {
    display: flex;
    gap: 0rem;

    &.mt-3 {
      margin-top: 2rem;
    }

    .title {
      width: 13%;

      p {
        font-size: 0.838rem;
      }
    }

    .detail {
      width: 80%;

      .display-text {
        font-size: 0.813rem;
        color: $main-6;
        font-weight: 400;
        margin-bottom: 0.25rem;
      }

      p {
        text-align: justify;
        margin-bottom: 1.25rem;
      }

      &.custom-fields {
        ul {
          gap: 0.5rem;
          margin-left: 1rem;

          li {
            font-size: 0.838rem;
            line-height: 20px;
            list-style: disc;
          }
        }
      }
    }
  }
  .info {
    padding-bottom: 1rem;
    border-bottom: 1px solid #dfdad2;

    &.info-margin {
      margin-top: 1.5rem;
    }
  }
  .info-holder-header {
    font-size: 0.913rem;
    margin-bottom: 1.2rem;
  }
  .info-holder-sub-header {
    font-size: 0.838rem;
    margin-bottom: 1.2rem;
    color: #a09c95;
    font-weight: 600;
  }
}

.row {
  display: flex;
  text-align: left;
  flex-wrap: wrap;
  justify-content: space-between;

  .row-holder {
    width: 33.3%;
  }
}

.field {
  color: $main-6;
  font-weight: 400;
  font-size: 0.813rem;
  margin-bottom: 0.5rem;
}

.button-area {
  display: flex;
  justify-content: center;
  align-items: center;

  button {
    text-transform: uppercase;
    padding: 0 2rem !important;
  }
}

table {
  width: 100%;
  margin-top: 1.25rem;
  border-spacing: 0px;
  border-collapse: separate;

  thead > tr > th {
    color: $main-6;
    text-align: center;
    padding: 1rem 0rem;
    font-size: 0.813rem;
    border-bottom: 1px solid $onyx;

    &:nth-child(3) {
      text-align: left;
    }
    &:nth-child(4) {
      text-align: left;
    }
    &:nth-child(6) {
      text-align: left;
    }
  }

  tbody > tr > td {
    color: $main-6;
    text-align: center;
    padding: 0.5rem 0rem;
    font-size: 0.813rem;
    border-bottom: 1px solid $onyx;
    width: max-content;

    tfoot > tr > td {
      padding-top: 3rem;
    }

    &:nth-child(1) {
      width: 5rem;
      max-width: 5rem;
    }
    &:nth-child(2) {
      width: 10rem;
      max-width: 10rem;
    }
    &:nth-child(3) {
      text-align: left;
    }
    &:nth-child(4) {
      text-align: left;
    }
    &:nth-child(6) {
      text-align: left;
    }

    &.blank {
      width: 2rem;
    }
  }
}

@media (max-width: 1100px) {
  .required-info {
    transition: 1s ease-in;

    .title {
      width: 13%;
      p {
        font-size: 0.838rem;
        margin-bottom: 1rem;
      }
    }

    .row {
      .row-holder {
        width: 100%;
      }
    }
  }

  .field {
    flex-basis: 50%;
  }
}
@media (max-width: 982px) {
  .required-info {
    transition: 1s ease-in;

    .title {
      width: 18% !important;
      p {
        font-size: 0.838rem;
        margin-bottom: 1rem;
      }
    }
  }
}
@media (max-width: 649px) {
  .required-info {
    flex-direction: column;
    transition: 1s ease-in;

    .title {
      width: 100% !important;
      p {
        font-size: 0.838rem;
        margin-bottom: 1rem;
      }
    }
  }
}
</style>
